import {
  BuildingIcon,
  CaretDownIconSmall,
  CaretUpIconSmall,
  CheckIcon,
  EditIcon,
  ExternalLinkIcon,
  FamilyIcon,
  Heading,
  IconButton,
  IdIcon,
  Modal,
  NakedButton,
  SecondaryButton,
  Text,
  TextField,
  TextLink,
  useFormField,
  useModalControl,
  XIcon,
} from '@weave/design-system';
import { theme } from '@weave/theme-original';
import React, { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { CopyToClipboard } from 'react-copy-to-clipboard';
import { API as IdentityService } from '@weave/schema-gen-ts/dist/apis/api/identity/v1/api.pb';
import { ReactComponent as MedicalFilledIcon } from '../../../images/medical-filled.svg';
import { REACT_APP_API_URL } from 'config/app';

import { getTenantURL } from '../../../helpers/pds';
import { DataCenters, LocationModel, LocationV2 } from '../../../redux/actions/location';
import { selectCurrentLocation } from '../../../redux/actions/location';
import { CustomAxios } from '../../../redux/axios';
import { SyncAppMultiCount } from '../../sync-app/sync-app-settings/integrations/sync-app-multi-count.component';
import { SidebarSyncApp } from '../sidebar-sync-app/sidebar-sync-app.component';

import {
  GetResponse,
  MessagingSettings,
  SetResponse,
} from '@weave/schema-gen-ts/dist/schemas/messaging/settings/v1/service.pb';
import {
  DeviceInclude_Enum,
  ListDevice,
} from '@weave/schema-gen-ts/dist/schemas/phone/devices/v2/devices.pb';
import { SettingType_Enum } from '@weave/schema-gen-ts/dist/schemas/messaging/settings/v1/settings.pb';

import { OrgService } from '@weave/schema-gen-ts/dist/schemas/organization/v1/org.pb';
import { GetLocationRegistrationStatusResponse } from '@weave/schema-gen-ts/dist/schemas/tendlc/coordinator/v1/coordinator_service.pb';
import {
  TCRBrandFriendlyRegistrationStatus,
  getFriendlyRegistrationStatus,
  getLocationRegistrationStatus,
  getLocationTCRBrand,
  getTCRBrandLockStatus,
} from 'apis/tendlc';
import {
  TenDLCBizStatusInfoButton,
  TenDLCPhoneStatusInfoButton,
} from 'components/tendlc';
import { authorizedFetch } from 'helpers/utils';
import { ContractedBundleName } from '../../../components/salesforce/contracted-bundle-name';
import { LocationSearchState } from '../../../redux/actions/location/location-search';
import { DevicesQueries } from '../../../apis/devices/v2/queries';
import {
  LocationEdit,
  LocationParentageModal,
  LocationSearchContainer,
} from '../../location';
import {
  accountLinkStyle,
  buildingIconStyle,
  clipboardDiv,
  dropdownContainerStyle,
  iconButtonSvgPrimary,
  iconPointerStyle,
  iconSmall,
  labelFlex,
  labelFlexRight,
  labelInfoDiv,
  mLeftSpacing,
  multiLocationDropdown,
  multiLocationTextStyle,
  sectionIconStyle,
  sidebar,
  statusDot,
} from '../location-sidebar.styles';
import { LocationStatusTypes } from '../location-sidebar.types';
import { LocationStatus, WarningAlert } from '../location-status/location-status';
import { AuthedUser } from '../../../redux/actions/auth/auth.types';
import { useAlert } from '@weave/alert-system';
import { LocationHierarchy } from './hierarchy.component';

interface Props {
  currentUser: AuthedUser;
  currentLocation: LocationModel | null;
  currentLocationV2: LocationV2 | null;
  isMultiOfficeBeta: boolean;
  childrenAccounts: LocationSearchState['locationChildren'];
  setLocation: (locationId) => void;
  allowParentageEdit: boolean;
  hasDatasources: boolean;
  departmentsEnabled: boolean;
  softphonesEnabled: boolean;
}

interface State {
  nameCopied: boolean;
  childSlugNameCopied: { [slug: string]: boolean };
  slugCopied: boolean;
  locationIdCopied: boolean;
  orgIdCopied: boolean;
  phoneTenantIdCopied: boolean;
  parentIdCopied: boolean;
  isModalOpen: boolean;
  isParentageModalOpen: boolean;
  childIdsCopied: any;
  multiLocationDropdownOpen: boolean;
  parentLocationName: string;
  orgId: string;
  orgName: string;
  tcrBizStatus?: TCRBrandFriendlyRegistrationStatus;
  tcrPhoneStatus: GetLocationRegistrationStatusResponse;
  tcrFetching: boolean;
  truelarkBusinessId?: string;
  aiAssistantEnabled?: boolean;
  isEditingBusinessId: boolean;
}

const ACTIVE_DEVICE_WINDOW_MS = 5 * 24 * 60 * 60 * 1000;

export class LocationSidebar extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);

    this.state = {
      nameCopied: false,
      childSlugNameCopied: {},
      slugCopied: false,
      locationIdCopied: false,
      orgIdCopied: false,
      phoneTenantIdCopied: false,
      parentIdCopied: false,
      isModalOpen: false,
      isParentageModalOpen: false,
      childIdsCopied: {},
      multiLocationDropdownOpen: false,
      parentLocationName: '',
      orgId: '',
      orgName: '',
      tcrBizStatus: undefined,
      tcrPhoneStatus: { registered: false, phoneNumbers: [], defaultSmsNumber: '' },
      tcrFetching: false,
      truelarkBusinessId: '',
      aiAssistantEnabled: undefined,
      isEditingBusinessId: false,
    };
  }

  tcrMap = {
    TCR_BRAND_IDENTITY_STATUS_SELF_DECLARED: 'Unverified',
    TCR_BRAND_IDENTITY_STATUS_VERIFIED: 'Verified',
    TCR_BRAND_IDENTITY_STATUS_VETTED_VERIFIED: 'Verified',
    TCR_BRAND_IDENTITY_STATUS_UNSPECIFIED: 'Unverified',
    TCR_BRAND_IDENTITY_STATUS_UNVERIFIED: 'Unverified',
  };

  componentDidMount() {
    this.loadParentLocation();
    this.loadOrgName();
    this.loadTcrStatuses();
    this.loadTruelarkBusinessId();
  }

  componentDidUpdate(prevProps) {
    if (this.props.currentLocation?.ParentID !== prevProps.currentLocation?.ParentID) {
      this.loadParentLocation();
    }

    if (
      this.props.currentLocation?.LocationID !== prevProps.currentLocation?.LocationID
    ) {
      this.loadTcrStatuses();
      this.loadOrgName();
      this.loadTruelarkBusinessId();
    }
  }

  loadParentLocation = async () => {
    if (this.props.currentLocation && this.props.currentLocation.ParentID) {
      try {
        const response = await CustomAxios.get(
          `support/v1/locations/${this.props.currentLocation.ParentID}`
        );
        this.setState({ parentLocationName: response.data.data.Name });
      } catch {
        this.setState({ parentLocationName: '' });
      }
    }
  };

  loadOrgName = async () => {
    if (this.props.currentLocation && this.props.currentLocation.LocationID) {
      try {
        const orgId = this.props.currentLocationV2?.orgId;

        if (orgId) {
          this.setState({ orgId });
          const org = await IdentityService.GetOrg(authorizedFetch({ version: 2 }), {
            orgId,
          });

          this.setState({ orgName: org.name ?? '' });
        }
      } catch {
        this.setState({ orgName: '' });
      }
    }
  };

  loadTruelarkBusinessId = async () => {
    if (this.props.currentLocation && this.props.currentLocation.LocationID) {
      try {
        const response: GetResponse = await MessagingSettings.Get(authorizedFetch(), {
          orgId: this.props.currentLocationV2?.orgId ?? '',
          locationId: this.props.currentLocation.LocationID,
          settingType: SettingType_Enum['TRUELARK_LOCATION_SETTING'],
        });

        if ('truelarkLocationSetting' in response.setting) {
          this.setState({
            truelarkBusinessId: response.setting.truelarkLocationSetting.businessId,
            aiAssistantEnabled: response.setting.truelarkLocationSetting.businessId
              ? response.setting.truelarkLocationSetting.aiAssistantEnabled
              : undefined,
          });
        }
      } catch (err) {
        console.error(err);
      }
    }
  };

  setTruelarkBusinessId = async (businessId: string) => {
    try {
      const response: SetResponse = await MessagingSettings.Set(authorizedFetch(), {
        orgId: this.props.currentLocationV2?.orgId ?? '',
        locationId: this.props.currentLocation?.LocationID,
        settingType: SettingType_Enum['TRUELARK_LOCATION_SETTING'],
        setting: {
          truelarkLocationSetting: {
            businessId,
            aiAssistantEnabled: this.state.aiAssistantEnabled ?? false,
          },
        },
        modifiedBy: this.props.currentUser.user_id,
      });
      if ('truelarkLocationSetting' in response.setting) {
        this.setState({
          truelarkBusinessId: response.setting.truelarkLocationSetting.businessId,
          aiAssistantEnabled: response.setting.truelarkLocationSetting.businessId
            ? response.setting.truelarkLocationSetting.aiAssistantEnabled
            : undefined,
        });
      }
    } catch (err) {
      console.error(err);
    }
  };

  loadTcrStatuses = async () => {
    if (this.props.currentLocation) {
      // Load Business Status
      try {
        this.setState({ tcrFetching: true });
        const locationTcrBrand = await getLocationTCRBrand({
          locationId: this.props.currentLocation.LocationID,
        });
        if (locationTcrBrand.tcrBrand.brandId) {
          const lockStatusResponse = await getTCRBrandLockStatus({
            brandId: locationTcrBrand.tcrBrand.brandId,
          });
          this.setState({
            tcrBizStatus: getFriendlyRegistrationStatus({
              locked: lockStatusResponse.locked ?? false,
              identityStatus: locationTcrBrand.tcrBrand?.identityStatus,
              country: locationTcrBrand.tcrBrand?.country,
              entityType: locationTcrBrand.tcrBrand?.entityType,
            }),
          });
        } else {
          this.setState({
            tcrBizStatus: TCRBrandFriendlyRegistrationStatus.UNREGISTERED,
          });
        }
      } catch {
        this.setState({ tcrBizStatus: undefined });
      }

      // Load Phone Status
      try {
        const response = await getLocationRegistrationStatus({
          locationId: this.props.currentLocation.LocationID,
        });
        if (response) {
          this.setState({
            tcrPhoneStatus: response,
          });
        }
      } catch {
        this.setState({
          tcrPhoneStatus: { registered: false, phoneNumbers: [], defaultSmsNumber: '' },
        });
      } finally {
        this.setState({ tcrFetching: false });
      }
    }
  };

  toggleModal = () => {
    this.setState({ isModalOpen: !this.state.isModalOpen });
  };

  toggleParentageModal = () => {
    this.setState({ isParentageModalOpen: !this.state.isParentageModalOpen });
  };

  toggleMultiLocationDropdownOpen = () => {
    this.setState({ multiLocationDropdownOpen: !this.state.multiLocationDropdownOpen });
  };

  copiedLocationNameText = () => {
    this.setState({ nameCopied: true });
    setTimeout(() => {
      this.setState({ nameCopied: false });
    }, 3000);
  };

  copiedChildSlugText = (slug: string) => {
    this.setState((prevState) => ({
      childSlugNameCopied: { ...prevState.childSlugNameCopied, [slug]: true },
    }));
    setTimeout(() => {
      this.setState((prevState) => ({
        childSlugNameCopied: { ...prevState.childSlugNameCopied, [slug]: false },
      }));
    }, 3000);
  };

  copiedSlugText = () => {
    this.setState({ slugCopied: true });
    setTimeout(() => {
      this.setState({ slugCopied: false });
    }, 3000);
  };

  copiedLocationIdText = () => {
    this.setState({ locationIdCopied: true });
    setTimeout(() => {
      this.setState({ locationIdCopied: false });
    }, 3000);
  };

  copiedOrgIdText = () => {
    this.setState({ orgIdCopied: true });
    setTimeout(() => {
      this.setState({ orgIdCopied: false });
    }, 3000);
  };

  copiedPhoneTenantId = () => {
    this.setState({ phoneTenantIdCopied: true });
    setTimeout(() => {
      this.setState({ phoneTenantIdCopied: false });
    }, 3000);
  };

  getHomerURL = (dataCenter: DataCenters): string => {
    if (dataCenter === DataCenters.CentralIA) {
      return 'https://homer-us-central1.weavephone.net/';
    } else if (dataCenter === DataCenters.EasternVA) {
      return 'https://homer-us-east4.weavephone.net/';
    } else if (dataCenter === DataCenters.PacificCA) {
      return 'https://homer-us-west2.weavephone.net/';
    }

    return '';
  };

  getLocationStatus = (
    locationId: string,
    phoneTenantId: string,
    parentId: string
  ): LocationStatusTypes | '' => {
    const hasChildren = this.props.childrenAccounts.length > 0;

    const isDataBetaParent = !parentId && !phoneTenantId && hasChildren;
    if (isDataBetaParent) return 'Data Beta Parent';

    const isDataBetaLocation = parentId && !phoneTenantId && !hasChildren;
    if (isDataBetaLocation) return 'Data Beta Location';

    const isSingleTenant = !parentId && !phoneTenantId && locationId && !hasChildren;
    if (isSingleTenant) return 'Single Tenant';

    const isUnifyChild = parentId && phoneTenantId && !isDataBetaLocation && !hasChildren;
    if (isUnifyChild) return 'Unify Child';

    const isUnifyParent = (!parentId && !!phoneTenantId) || hasChildren;
    if (isUnifyParent) return 'Unify Parent';

    return '';
  };

  getLocationWarnings = (): string[] => {
    if (this.props.currentLocation) {
      const { LocationID, PhoneTenantID, ParentID } = this.props.currentLocation;
      const locationStatus = this.getLocationStatus(
        LocationID,
        PhoneTenantID ?? '',
        ParentID ?? ''
      );
      if (locationStatus === 'Data Beta Parent') {
        const childHasPhoneTenantId = this.props.childrenAccounts.some(
          (child) => child.PhoneTenantID
        );

        return childHasPhoneTenantId ? ['A Unify Child configuration found'] : [];
      } else if (locationStatus === 'Unify Parent') {
        const warningMessage: string[] = [];
        const childPhoneTenantIdMissing = this.props.childrenAccounts.some(
          (child) => !child.PhoneTenantID
        );
        const hasMisMatchedPhoneTenantId = this.props.childrenAccounts.some(
          (child) => child.PhoneTenantID !== PhoneTenantID
        );

        if (childPhoneTenantIdMissing)
          warningMessage.push('PhoneTenantID missing in Unify child location');

        if (hasMisMatchedPhoneTenantId)
          warningMessage.push(
            'PhoneTenantID for child location should be same as parent'
          );

        return warningMessage;
      }
    }
    return [];
  };

  updateOrgName = (newOrgName: string) => {
    this.setState({ orgName: newOrgName });
  };

  render() {
    if (!this.props.currentLocation) {
      return null;
    }

    const { LocationID, PhoneTenantID, ParentID, Slug, Name, DataCenter, Active } =
      this.props.currentLocation;

    const orgId = this.props.currentLocationV2?.orgId;

    const childrenAccounts = this.props.childrenAccounts.sort((a, b) =>
      a.Name.localeCompare(b.Name, undefined, { numeric: true, sensitivity: 'base' })
    );
    const homerUrl = this.getHomerURL(DataCenter);
    if (!LocationID) {
      return null;
    }

    const { url: tenantUrl, title: tenantTitle } = getTenantURL(
      PhoneTenantID as string,
      LocationID as string
    );

    return (
      <div css={sidebar}>
        <LocationSearchContainer />
        <div css={labelInfoDiv(theme, 3)}>
          <Heading level={2}>Location Info</Heading>
          {orgId && <LocationHierarchy orgId={orgId} />}

          {this.props.allowParentageEdit ? (
            <IconButton
              label="Edit Parentage"
              showLabelOnHover={true}
              css={sectionIconStyle}
              onClick={this.toggleParentageModal}
            >
              <IdIcon />
            </IconButton>
          ) : null}
          <IconButton
            label="Edit Location"
            onClick={this.toggleModal}
            css={iconButtonSvgPrimary}
          >
            <EditIcon />
          </IconButton>
        </div>
        <CopyToClipboard text={Name} onCopy={this.copiedLocationNameText}>
          <div css={clipboardDiv} title="Copy to clipboard">
            <BuildingIcon css={buildingIconStyle} />
            <Heading level={2} css={{ wordBreak: 'break-all' }}>
              {Name}
            </Heading>
          </div>
        </CopyToClipboard>
        {this.state.nameCopied && (
          <Text color="success" size="medium">
            Copied!
          </Text>
        )}

        <div css={labelInfoDiv(theme)}>
          <Text as="span" size="medium" color="light">
            Departments
          </Text>
          <div css={labelFlexRight}>
            <div
              css={statusDot(theme, this.props.departmentsEnabled ? 'success' : 'error')}
            />
            <Text as="span" size="medium">
              {this.props.departmentsEnabled ? 'Enabled' : 'Disabled'}
            </Text>
          </div>
        </div>
        {this.props.softphonesEnabled && (
          <div css={labelInfoDiv(theme)}>
            <Text as="span" size="medium" color="light">
              Softphones
            </Text>
            <div css={labelFlexRight}>
              <div css={statusDot(theme, 'success')} />
              <Text as="span" size="medium">
                Enabled
              </Text>
            </div>
          </div>
        )}
        <E911Status
          tenantId={PhoneTenantID ?? ''}
          selectedLocationIds={[
            LocationID,
            ...childrenAccounts.map((child) => child.LocationID),
          ]}
        />
        {this.props.isMultiOfficeBeta ? (
          <React.Fragment>
            <hr />

            {ParentID && (
              <>
                <div css={labelInfoDiv(theme, 0)}>
                  <Text as="span" weight="bold" css={multiLocationTextStyle}>
                    Multi-Location
                  </Text>
                  <Text as="span" weight="bold" size="medium">
                    Child
                  </Text>
                </div>

                <LocationStatus
                  locationStatus={this.getLocationStatus(
                    LocationID,
                    PhoneTenantID ?? '',
                    ParentID
                  )}
                />

                <div css={labelInfoDiv}>
                  <Text as="span" size="medium" color="light">
                    Parent Account
                  </Text>
                </div>

                <NakedButton
                  css={accountLinkStyle}
                  onClick={() => {
                    this.props.setLocation(ParentID);
                  }}
                >
                  {this.state.parentLocationName || ParentID}
                </NakedButton>

                {this.state.orgId && (
                  <>
                    <div css={labelInfoDiv}>
                      <Text as="span" size="medium" color="light">
                        Organization Account
                      </Text>
                    </div>
                    <NakedButton css={accountLinkStyle}>
                      {this.state.orgName || this.state.orgId}
                    </NakedButton>
                  </>
                )}
              </>
            )}

            {!ParentID && (
              <React.Fragment>
                {' '}
                <div css={labelInfoDiv(theme, 0)}>
                  <Text weight="bold" css={multiLocationTextStyle}>
                    Multi-Location
                  </Text>
                  <Text as="span" weight="bold" size="medium" css={labelFlexRight}>
                    {childrenAccounts.length} Locations
                  </Text>
                </div>
                <LocationStatus
                  locationStatus={this.getLocationStatus(
                    LocationID,
                    PhoneTenantID ?? '',
                    ''
                  )}
                  hasChildren={childrenAccounts.length > 0}
                />
                <SyncAppMultiCount />
                {this.getLocationWarnings().map((warningMessage) => (
                  <WarningAlert title={warningMessage} />
                ))}
                <div
                  css={dropdownContainerStyle}
                  onClick={() => {
                    this.toggleMultiLocationDropdownOpen();
                  }}
                >
                  <div css={labelInfoDiv(theme, 0)}>
                    <FamilyIcon />

                    <Text as="span" weight="bold" css={mLeftSpacing}>
                      {Name}
                    </Text>
                  </div>
                  {!this.state.multiLocationDropdownOpen && (
                    <CaretDownIconSmall color="light" css={iconPointerStyle} />
                  )}
                  {this.state.multiLocationDropdownOpen && (
                    <CaretUpIconSmall color="light" css={iconPointerStyle} />
                  )}
                </div>
                {this.state.multiLocationDropdownOpen && (
                  <div css={multiLocationDropdown}>
                    <Text size="medium" color="light">
                      Parent Account
                    </Text>

                    <NakedButton
                      css={accountLinkStyle}
                      onClick={() => {
                        this.props.setLocation(LocationID);
                      }}
                    >
                      {Name}
                    </NakedButton>

                    <Text size="medium" color="light">
                      Child Accounts
                    </Text>

                    {childrenAccounts.map((child) => {
                      return (
                        <React.Fragment>
                          <NakedButton
                            css={accountLinkStyle}
                            onClick={() => {
                              this.props.setLocation(child.LocationID);
                            }}
                            key={child.LocationID}
                          >
                            {child.Name}
                          </NakedButton>
                          <CopyToClipboard
                            text={child.Slug}
                            onCopy={() => this.copiedChildSlugText(child.Slug)}
                          >
                            <div css={[iconPointerStyle, labelInfoDiv(theme)]}>
                              <Text as="span" size="medium">
                                {child.Slug}
                              </Text>
                            </div>
                          </CopyToClipboard>
                          {this.state.childSlugNameCopied[child.Slug] && (
                            <Text color="success" size="medium">
                              Copied!
                            </Text>
                          )}
                          <br />
                        </React.Fragment>
                      );
                    })}
                  </div>
                )}
              </React.Fragment>
            )}
          </React.Fragment>
        ) : (
          <LocationStatus
            locationStatus={this.getLocationStatus(
              LocationID,
              PhoneTenantID ?? '',
              ParentID ?? ''
            )}
            hasChildren={childrenAccounts.length > 0}
          />
        )}
        <hr />
        <div css={labelInfoDiv(theme, 0)}>
          <Text as="span" size="medium" color="light">
            Status
          </Text>
          <div css={labelFlexRight}>
            <div css={statusDot(theme, Active ? 'success' : 'error')} />
            <Text as="span" size="medium">
              {Active ? 'Active' : 'Inactive'}
            </Text>
          </div>
        </div>
        <CopyToClipboard text={Slug} onCopy={this.copiedSlugText}>
          <div css={[iconPointerStyle, labelInfoDiv(theme)]}>
            <Text as="span" size="medium" color="light" title="Copy to clipboard">
              Slug
            </Text>
            <Text as="span" size="medium">
              {Slug}
            </Text>
          </div>
        </CopyToClipboard>
        {this.state.slugCopied && (
          <Text color="success" size="medium">
            Copied!
          </Text>
        )}
        <div css={labelInfoDiv}>
          <div css={labelFlex}>
            {homerUrl && (
              <IconButton
                label="Go to Homer portal"
                size="small"
                to={homerUrl}
                rel="noopener noreferrer"
                target="_blank"
                css={mLeftSpacing(theme, -1)}
              >
                <ExternalLinkIcon css={iconSmall} />
              </IconButton>
            )}
            <Text as="span" size="medium" color="light">
              Data Center
            </Text>
          </div>

          <Text as="span" size="medium">
            {DataCenter}
          </Text>
        </div>
        <CopyToClipboard text={LocationID} onCopy={this.copiedLocationIdText}>
          <div css={[iconPointerStyle, labelInfoDiv(theme)]}>
            <Text as="span" size="medium" color="light" title="Copy to clipboard">
              Location ID
            </Text>
            <Text as="span" size="medium">
              {LocationID}
            </Text>
          </div>
        </CopyToClipboard>
        {this.state.locationIdCopied && (
          <Text color="success" size="medium">
            Copied!
          </Text>
        )}
        <CopyToClipboard text={orgId} onCopy={this.copiedOrgIdText}>
          <div css={[iconPointerStyle, labelInfoDiv(theme)]}>
            <Text as="span" size="medium" color="light" title="Copy to clipboard">
              Org ID
            </Text>
            <Text as="span" size="medium">
              {orgId}
            </Text>
          </div>
        </CopyToClipboard>
        {this.state.orgIdCopied && (
          <Text color="success" size="medium">
            Copied!
          </Text>
        )}
        <div css={[iconPointerStyle, labelInfoDiv(theme)]}>
          <Text as="span" size="medium" color="light" title="Copy to clipboard">
            <CopyToClipboard text={tenantTitle} onCopy={this.copiedPhoneTenantId}>
              <span>Phone Tenant ID</span>
            </CopyToClipboard>
          </Text>
          <Text as="span" size="medium">
            <CopyToClipboard text={tenantTitle} onCopy={this.copiedPhoneTenantId}>
              <span>{tenantTitle}</span>
            </CopyToClipboard>
            <TextLink to={tenantUrl} target="_blank">
              <ExternalLinkIcon css={iconSmall} />
            </TextLink>
          </Text>
        </div>
        {this.state.phoneTenantIdCopied && (
          <Text color="success" size="medium">
            Copied!
          </Text>
        )}
        {this.state.tcrBizStatus && !this.state.tcrFetching && (
          <div css={[labelInfoDiv(theme)]}>
            <Text as="span" size="medium" color="light">
              10DLC Biz Status
            </Text>
            <Text as="span" size="medium">
              {`${this.state.tcrBizStatus}`}
            </Text>
            <TenDLCBizStatusInfoButton />
          </div>
        )}
        {this.state.tcrPhoneStatus && !this.state.tcrFetching && (
          <div css={[labelInfoDiv(theme)]}>
            <Text as="span" size="medium" color="light">
              10DLC Phone Status
            </Text>
            <Text size="medium" css={{ margin: 0 }}>
              {this.state.tcrBizStatus === TCRBrandFriendlyRegistrationStatus.CANADA ||
              this.state.tcrBizStatus ===
                TCRBrandFriendlyRegistrationStatus.SOLE_PROPRIETOR
                ? this.state.tcrBizStatus
                : `${
                    this.state.tcrPhoneStatus.registered ? 'Registered' : 'Unregistered'
                  }`}
            </Text>
            <TenDLCPhoneStatusInfoButton />
          </div>
        )}

        <div css={[labelInfoDiv(theme)]}>
          <BusinessIdField
            truelarkBusinessId={this.state.truelarkBusinessId}
            updateBusinessId={this.setTruelarkBusinessId}
          />
        </div>

        <div css={[labelInfoDiv(theme)]}>
          <Text as="span" size="medium" color="light">
            Contracted Bundle
          </Text>
          <ContractedBundleName as="span" size="medium" locationId={LocationID} />
        </div>
        <hr />

        {!ParentID && <SidebarSyncApp />}

        <Modal show={this.state.isModalOpen} onClose={this.toggleModal} maxWidth={480}>
          <LocationEdit
            onClose={this.toggleModal}
            orgName={this.state.orgName}
            orgId={this.state.orgId ?? ''}
            updateOrgName={this.updateOrgName}
          />
        </Modal>
        <Modal
          show={this.state.isParentageModalOpen}
          onClose={this.toggleParentageModal}
          maxWidth={480}
        >
          <LocationParentageModal
            locationID={LocationID}
            parentLocationID={ParentID ?? ''}
            phoneTenantID={PhoneTenantID ?? ''}
            onClose={this.toggleParentageModal}
            hasChildren={childrenAccounts.length > 0}
            getLocationStatus={this.getLocationStatus}
          />
        </Modal>
      </div>
    );
  }
}

const getE911StatusText = (
  unassignedDeviceCount: number
): { firstLine: string; secondLine?: string } => {
  if (unassignedDeviceCount === 0) {
    return { firstLine: 'All devices assigned' };
  }

  if (unassignedDeviceCount === 1) {
    return {
      firstLine: '1 active device',
      secondLine: 'w/o E911 address',
    };
  }

  return {
    firstLine: `${unassignedDeviceCount} active devices`,
    secondLine: 'w/o E911 addresses',
  };
};

const E911Status = ({
  tenantId,
  selectedLocationIds,
}: {
  tenantId: string;
  selectedLocationIds: string[];
}) => {
  const locationId = useSelector(selectCurrentLocation)?.LocationID ?? 'portal';

  const { data: devices = [], isLoading } = DevicesQueries.useGetDevicesList(
    {
      locationIds: selectedLocationIds,
      include: [DeviceInclude_Enum.E911_ADDRESS, DeviceInclude_Enum.REGISTRATION],
      ...(tenantId ? { tenantId } : {}),
    },
    {
      enabled: selectedLocationIds.length > 0,
      select: (data) => data.devices ?? [],
      staleTime: 0,
    }
  );

  const unassignedDeviceCount = useMemo(() => {
    const now = Date.now();

    return devices.reduce((count, device: ListDevice) => {
      if (device.e911Address?.id) {
        return count;
      }

      const hasRecentRegistration = (device.sipProfiles ?? []).some((profile) => {
        const lastConnectedAt = Date.parse(profile.registration?.lastConnected ?? '');
        return (
          Number.isFinite(lastConnectedAt) &&
          now - lastConnectedAt < ACTIVE_DEVICE_WINDOW_MS
        );
      });

      return hasRecentRegistration ? count + 1 : count;
    }, 0);
  }, [devices]);
  const medicalIconColor =
    unassignedDeviceCount >= 1 ? theme.colors.errorPressed : theme.colors.success;

  const statusText = isLoading
    ? { firstLine: 'Loading...' }
    : getE911StatusText(unassignedDeviceCount);

  const e911PortalUrl = `https://app.${
    REACT_APP_API_URL.includes('dev') ? 'weavedev.net' : 'getweave.com'
  }/${locationId}/phone/e911`;

  return (
    <div
      css={[
        labelInfoDiv(theme),
        {
          alignItems: 'flex-start',
          '> div:last-of-type': {
            flex: '0 0 164px',
            width: 164,
            minWidth: 164,
            maxWidth: 164,
          },
        },
      ]}
    >
      <Text as="span" size="medium" color="light">
        E911 Status
      </Text>
      <div
        css={{
          display: 'inline-flex',
          alignItems: 'flex-start',
          justifyContent: 'flex-end',
          gap: 4,
          width: '100%',
        }}
      >
        <MedicalFilledIcon
          aria-label="medical icon"
          css={{ width: 16, height: 16, color: medicalIconColor, marginTop: 2 }}
        />
        <div
          css={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-end',
            textAlign: 'right',
            lineHeight: '18px',
          }}
        >
          <Text as="span" size="medium">
            {statusText.firstLine}
          </Text>
          {statusText.secondLine && (
            <Text as="span" size="medium">
              {statusText.secondLine}
            </Text>
          )}
          {unassignedDeviceCount >= 1 && (
            <TextLink
              to={e911PortalUrl}
              target="_blank"
              rel="noopener noreferrer"
              css={{
                position: 'absolute',
                left: 295,
              }}
            >
              <ExternalLinkIcon css={iconSmall} />
            </TextLink>
          )}
        </div>
      </div>
    </div>
  );
};

const BusinessIdField = ({
  truelarkBusinessId,
  updateBusinessId,
}: {
  truelarkBusinessId?: string;
  updateBusinessId: (id: string) => Promise<void>;
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const field = useFormField({ type: 'text', value: truelarkBusinessId }, [
    truelarkBusinessId,
  ]);
  const alerts = useAlert();

  const onSubmit = (id: string) => {
    try {
      if (id !== truelarkBusinessId) {
        updateBusinessId(id);
        alerts.success('Business ID updated successfully.');
      }
    } catch (error) {
      alerts.error('Failed to update Business ID.');
    } finally {
      setIsEditing(false);
    }
  };

  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: 'auto 1fr auto',
        gap: 8,
        alignItems: 'center',
      }}
    >
      <Text as="span" size="medium" color="light">
        TL Business ID
      </Text>

      <div style={{ justifySelf: 'end' }}>
        {isEditing ? (
          <TextField
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                onSubmit(field.value);
              }
            }}
            css={{ width: 100 }}
            name="business-id"
            label=""
            {...field}
          />
        ) : (
          <Text as="span" size="medium">
            {truelarkBusinessId}
          </Text>
        )}
      </div>
      {isEditing ? (
        <div>
          <IconButton
            label="Edit Business ID"
            onClick={() => {
              setIsEditing(false);
            }}
            size="small"
          >
            <XIcon size={16} />
          </IconButton>
          <IconButton
            label="Edit Business ID"
            onClick={() => {
              onSubmit(field.value);
            }}
            css={iconButtonSvgPrimary}
            size="small"
          >
            <CheckIcon size={16} />
          </IconButton>
        </div>
      ) : (
        <IconButton
          label="Edit Business ID"
          onClick={() => {
            setIsEditing(true);
          }}
          css={iconButtonSvgPrimary}
          size="small"
        >
          <EditIcon size={16} />
        </IconButton>
      )}
    </div>
  );
};
